import {createStore as createZustandStore} from 'zustand/vanilla';
import {useStore} from 'zustand';
import logger from 'src/utils/logger';
import type {ChatPanelHandle} from './chat-panel';


export type AskAIState = {
  handle: ChatPanelHandle | null;
  product: string; // product/source key for AskAI
  threadId: string | null;
  setHandle: (h: ChatPanelHandle | null) => void;
  setProduct: (p: string) => void;
  setThreadId: (id: string | null) => void;
  clearThreadId: () => void;
};

const THREAD_ID_STORAGE_KEY = 'askai:threadId';

export const askAIStore = createZustandStore<AskAIState>((set) => {
  let initialThreadId = null;
  try {
    initialThreadId = localStorage.getItem(THREAD_ID_STORAGE_KEY) ?? null;
  } catch (e) {
    logger.error('Error getting initial thread id from localStorage:', e);
  }

  return {
    handle: null,
    product: 'workflow', //default product. can be updated later.
    threadId: initialThreadId,
    setHandle: (value) => set({handle: value}),
    setProduct: (value) => set({product: value}),
    setThreadId: (id) => {
      if (typeof localStorage !== 'undefined') {
        if (id) {
          localStorage.setItem(THREAD_ID_STORAGE_KEY, id);
        } else {
          localStorage.removeItem(THREAD_ID_STORAGE_KEY);
        }
      }
      set({threadId: id});
    },
    clearThreadId: () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(THREAD_ID_STORAGE_KEY);
      }
      getChatPanelHandle()?.clearMessages();
      set({threadId: null});
    },
  };
});

// Vanilla accessors (usable outside React)
export function getChatPanelHandle(): ChatPanelHandle | null {
  return askAIStore.getState().handle;
}

export function setChatPanelHandle(value: ChatPanelHandle | null) {
  askAIStore.getState().setHandle(value);
}

export function getProduct(): string | undefined {
  return askAIStore.getState().product;
}

export function setProduct(value?: string) {
  const next = value ?? askAIStore.getState().product;
  askAIStore.getState().setProduct(next);
}
export function registerChatPanel(value: ChatPanelHandle | null) {
  setChatPanelHandle(value);
}

export type PanelMode = 'floating' | 'attached' | 'fullscreen' | 'hidden';

export function getChatPanelMode(): PanelMode | undefined {
  return askAIStore.getState().handle?.currentMode as PanelMode | undefined;
}

export function setChatPanelMode(mode: Exclude<PanelMode, 'attached'>) {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setMode(mode);
}

export function openChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setOpen(true);
}

export function closeChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setOpen(false);
}

// React selector helpers
export function useAskAIProduct(): string {
  return useStore(askAIStore, (s) => s.product);
}

export function useAskAIThreadId(): string | null {
  return useStore(askAIStore, (s) => s.threadId);
}

// Vanilla accessors for thread id
export function getThreadId(): string | null {
  return askAIStore.getState().threadId;
}

export function setThreadId(value: string | null) {
  askAIStore.getState().setThreadId(value);
}

export function clearThreadId() {
  askAIStore.getState().clearThreadId();
}

export function initNewThread({source}: {source: string}) {
  clearThreadId();
  askAIStore.getState().setProduct(source);
}
